import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, CalendarClock } from "lucide-react";

const d = (v) => (v ? new Date(v).toLocaleDateString("it-IT") : "—");
const BADGE = { aperto: "bg-amber-100 text-amber-800", esercitato: "bg-red-100 text-red-800", scaduto: "bg-slate-100 text-slate-600" };

const CC = { in_ritardo: ["bg-red-100 text-red-800", "In ritardo"], da_spedire: ["bg-amber-100 text-amber-800", "Da spedire"], spedita: ["bg-emerald-100 text-emerald-800", "Spedita"] };

function CopieCartacee() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [g, setG] = useState(5);
  const [fest, setFest] = useState("");
  const [naz, setNaz] = useState([]);
  useEffect(() => {
    api.get("/trattative/copia-cartacea/scadenzario").then(({ data }) => setRows(data));
    api.get("/trattative/copia-cartacea/config").then(({ data }) => { setG(data.giorni_lavorativi); setFest((data.festivita_extra || []).join(", ")); setNaz(data.festivita_nazionali || []); });
  }, []);
  const salva = async () => {
    try { const { data } = await api.put("/trattative/copia-cartacea/config", { giorni_lavorativi: Number(g), festivita_extra: fest.split(/[,\s]+/).filter(Boolean) });
      setFest(data.festivita_extra.join(", ")); toast.success("Impostazioni aggiornate per le nuove richieste"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  return (
    <div className="mt-10" data-testid="scad-copie">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
        <h2 className="text-xl font-bold text-slate-900">Copie cartacee richieste</h2>
        {user?.role === "admin" && <div className="flex items-center gap-2 text-sm"><span className="text-slate-600">Termine di spedizione</span>
          <input type="number" min={1} max={30} value={g} onChange={(e) => setG(e.target.value)} className="w-16 h-8 border rounded px-2" data-testid="scad-copie-giorni" /><span className="text-slate-600">giorni lavorativi</span>
          <button onClick={salva} className="h-8 px-3 border rounded-md bg-white" data-testid="scad-copie-giorni-salva">Salva termine e festività</button></div>}
      </div>
      <p className="text-xs text-slate-500 mb-2">Il termine salta sabati, domeniche, Pasquetta e le festività nazionali ({naz.map((x) => x.split("-").reverse().join("/")).join(", ")}).</p>
      {user?.role === "admin" && <div className="flex items-center gap-2 text-sm mb-3"><span className="text-slate-600 shrink-0">Festività locali (patroni)</span>
        <input value={fest} onChange={(e) => setFest(e.target.value)} placeholder="es. 07-15 per ogni anno, 2026-12-07 per una data singola" className="flex-1 h-8 border rounded px-2 text-xs" data-testid="scad-copie-festivita" /></div>}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm"><thead className="bg-slate-50 text-xs text-slate-500 text-left"><tr><th className="p-3">Cliente</th><th className="p-3">Indirizzo</th><th className="p-3">Richiesta</th><th className="p-3">Scadenza</th><th className="p-3">Stato</th></tr></thead>
          <tbody>{!rows.length && <tr><td colSpan={5} className="p-6 text-center text-slate-400" data-testid="scad-copie-vuoto">Nessuna richiesta</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} onClick={() => nav(`/trattative/${r.id}`)} className="border-t hover:bg-slate-50 cursor-pointer" data-testid={`scad-copia-${r.id}`}>
                <td className="p-3 font-medium text-slate-800">{r.cliente}<div className="text-xs text-slate-400">{r.codice}</div></td><td className="p-3 text-slate-600">{r.indirizzo}</td>
                <td className="p-3 text-slate-600">{d(r.richiesta_at)}</td><td className="p-3 font-medium">{d(r.scadenza)}</td>
                <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full ${CC[r.stato][0]}`}>{CC[r.stato][1]}{r.spedita_at && ` il ${d(r.spedita_at)}`}</span></td>
              </tr>))}</tbody></table>
      </div>
    </div>
  );
}

export default function ScadenzarioRecessi() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [f, setF] = useState("");
  useEffect(() => { api.get("/trattative/recessi/scadenzario").then(({ data }) => setRows(data)); }, []);
  const vis = (rows || []).filter((r) => !f || r.stato === f);
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <button onClick={() => nav("/trattative")} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-4" data-testid="scad-back"><ArrowLeft className="h-4 w-4" /> Trattative</button>
        <div className="flex items-end justify-between gap-4 flex-wrap mb-5">
          <div><div className="flex items-center gap-2 text-teal-700 text-xs font-semibold tracking-wide"><CalendarClock className="h-4 w-4" /> SCADENZARIO</div>
            <h1 className="text-3xl font-bold text-slate-900 mt-1">Recessi</h1>
            <p className="text-sm text-slate-500 mt-1">Contratti registrati con il termine di recesso: prima quelli ancora recedibili, in ordine di scadenza.</p></div>
          <select value={f} onChange={(e) => setF(e.target.value)} className="h-9 text-sm border border-slate-200 rounded-md px-2 bg-white" data-testid="scad-filtro">
            <option value="">Tutti</option><option value="aperto">Recesso possibile</option><option value="esercitato">Recesso esercitato</option><option value="scaduto">Termine scaduto</option>
          </select>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm" data-testid="scad-tabella">
            <thead className="bg-slate-50 text-xs text-slate-500 text-left"><tr><th className="p-3">Cliente</th><th className="p-3">Prodotto</th><th className="p-3">Decorrenza</th><th className="p-3">Scadenza</th><th className="p-3">Stato</th></tr></thead>
            <tbody>
              {rows === null && <tr><td colSpan={5} className="p-6 text-center text-slate-400">Caricamento…</td></tr>}
              {rows && !vis.length && <tr><td colSpan={5} className="p-6 text-center text-slate-400" data-testid="scad-vuoto">Nessun contratto</td></tr>}
              {vis.map((r) => (
                <tr key={r.id} onClick={() => nav(`/trattative/${r.id}`)} className="border-t hover:bg-slate-50 cursor-pointer" data-testid={`scad-riga-${r.id}`}>
                  <td className="p-3"><div className="font-medium text-slate-800">{r.cliente}</div><div className="text-xs text-slate-400">{r.codice}</div></td>
                  <td className="p-3 text-slate-600">{r.prodotto || "—"}</td>
                  <td className="p-3 text-slate-600">{d(r.contratto.decorrenza)} <span className="text-xs text-slate-400">({r.contratto.giorni_recesso} gg)</span></td>
                  <td className="p-3 font-medium">{d(r.contratto.scadenza)}</td>
                  <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full ${BADGE[r.stato] || ""}`}>{r.stato === "aperto" ? `${r.giorni_rimasti} giorni rimasti` : r.label}</span>
                    {!r.pagina_inviata_at && r.stato === "aperto" && <div className="text-[11px] text-amber-700 mt-0.5">pagina personale non inviata</div>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <CopieCartacee />
      </div>
    </div>
  );
}
